import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from "date-fns";
import { fr } from "date-fns/locale/fr";
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { CircularProgress } from '@mui/material';

type Programmation = {
  id: string;
  title: string;
  description: string;
  startDate: Date;
  endDate: Date;
  category: string;
  userId: string;
  createdAt: Date;
  updatedAt: Date;
  periods?: any[];
};

const PublicProgrammation = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [programmation, setProgrammation] = useState<Programmation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchPublicProgrammation = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const programmationRef = doc(db, 'programmations', id);
        const programmationDoc = await getDoc(programmationRef);

        if (!programmationDoc.exists()) {
          throw new Error('Programmation non trouvée');
        }

        const data = programmationDoc.data();
        
        // Vérifier si la programmation est marquée comme publique
        if (!data.isPublic) {
          throw new Error('Cette programmation n\'est pas accessible publiquement');
        }

        setProgrammation({
          id: programmationDoc.id,
          title: data.title,
          description: data.description || '',
          startDate: data.startDate?.toDate(),
          endDate: data.endDate?.toDate(),
          category: data.category || 'senior',
          userId: data.userId,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
          periods: data.periods || [],
        });
      } catch (err: any) {
        console.error('Erreur lors du chargement de la programmation:', err);
        setError(err.message || 'Une erreur est survenue');
      } finally {
        setLoading(false);
      }
    };

    fetchPublicProgrammation();
  }, [id]);

  const formatDate = (date: Date) => {
    return format(date, 'EEEE d MMMM yyyy', { locale: fr });
  };

  const getCategoryLabel = (category: string) => {
    const categories: { [key: string]: string } = {
      senior: 'Sénior',
      u19: 'U19',
      u17: 'U17',
      u15: 'U15',
      u13: 'U13',
      u11: 'U11',
      u9: 'U9',
      u7: 'U7',
    };
    return categories[category] || category;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <CircularProgress size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Retour à l'accueil
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!programmation) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-yellow-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                Programmation introuvable. Elle a peut-être été supprimée ou n'est plus accessible publiquement.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            {programmation.title}
          </h1>
          <div className="mt-2 flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-6">
            <div className="flex items-center text-sm text-gray-500">
              <svg
                className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                  clipRule="evenodd"
                />
              </svg>
              {formatDate(programmation.startDate)} - {formatDate(programmation.endDate)}
            </div>
            <div className="flex items-center text-sm text-gray-500">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {getCategoryLabel(programmation.category)}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Détails de la planification
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Vue d'ensemble du programme d'entraînement.
            </p>
          </div>

          <div className="px-4 py-5 sm:p-6">
            {programmation.description && (
              <div className="mb-8">
                <h4 className="text-sm font-medium text-gray-500">Description</h4>
                <div className="mt-1 text-sm text-gray-900 whitespace-pre-line">
                  {programmation.description}
                </div>
              </div>
            )}

            <div className="border-t border-gray-200 pt-6">
              <h4 className="text-sm font-medium text-gray-500">Planning des séances</h4>
              {/* Affichage complet du planning ici, sans restriction */}
              {programmation.periods && programmation.periods.length > 0 ? (
  <div className="overflow-x-auto -mx-2 sm:mx-0">
    <table className="min-w-full divide-y divide-blue-200 border border-blue-200 bg-white/90 shadow-sm">
      <thead>
        <tr className="bg-blue-50">
          <th className="p-1 sm:p-2 text-left text-xs font-medium text-blue-900 uppercase tracking-wider border border-blue-200">
            Période
          </th>
          <th className="p-1 sm:p-2 text-left text-xs font-medium text-blue-900 uppercase tracking-wider border border-blue-200">
            <div className="flex items-center">
              <svg className="h-4 w-4 mr-1 text-blue-700" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="#90caf9"/></svg>
              <span className="hidden sm:inline">Tactique</span>
              <span className="sm:hidden">Tact.</span>
            </div>
          </th>
          <th className="p-1 sm:p-2 text-left text-xs font-medium text-blue-900 uppercase tracking-wider border border-blue-200">
            <div className="flex items-center">
              <svg className="h-4 w-4 mr-1 text-blue-700" viewBox="0 0 24 24"><rect x="4" y="4" width="16" height="16" fill="#81d4fa"/></svg>
              <span className="hidden sm:inline">Technique</span>
              <span className="sm:hidden">Tech.</span>
            </div>
          </th>
          <th className="p-1 sm:p-2 text-left text-xs font-medium text-blue-900 uppercase tracking-wider border border-blue-200">
            <div className="flex items-center">
              <svg className="h-4 w-4 mr-1 text-green-700" viewBox="0 0 24 24"><rect x="4" y="4" width="16" height="16" fill="#a5d6a7"/></svg>
              <span className="hidden sm:inline">Athlétique</span>
              <span className="sm:hidden">Athl.</span>
            </div>
          </th>
          <th className="p-1 sm:p-2 text-left text-xs font-medium text-blue-900 uppercase tracking-wider border border-blue-200">
            <div className="flex items-center">
              <svg className="h-4 w-4 mr-1 text-purple-700" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="#ce93d8"/></svg>
              <span className="hidden sm:inline">Mental</span>
              <span className="sm:hidden">Ment.</span>
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        {programmation.periods.map((period: any, idx: number) => (
          <tr key={idx} className="bg-white/80 border-b border-blue-100">
            <td className="p-1 sm:p-2 border border-blue-200 align-top min-w-[120px] sm:min-w-[170px]">
              {period.title && (
                <div className="font-semibold text-blue-900 mb-1 text-xs sm:text-sm truncate max-w-[150px]" title={period.title}>
                  {period.title}
                </div>
              )}
              <div className="text-xs text-gray-500 mb-1 hidden sm:block">{period.comments}</div>
              <div className="flex gap-1 sm:gap-2 text-xs">
                <span className="inline-block bg-blue-50 rounded px-1 sm:px-2 py-0.5 sm:py-1 border border-blue-100">
                  {period.weeks} sem.
                </span>
                <span className="inline-block bg-blue-50 rounded px-1 sm:px-2 py-0.5 sm:py-1 border border-blue-100">
                  {period.sessions} séances
                </span>
              </div>
            </td>
            {(['tactique','technique','athletique','mental']).map((themeKey) => (
              <td key={themeKey} className="p-1 sm:p-2 border border-blue-200 align-top min-w-[80px] sm:min-w-[140px]">
                <div className="font-medium text-blue-800 mb-1 capitalize text-xs sm:text-sm hidden sm:block">{themeKey}</div>
                {/* Itérer sur les deux thèmes de chaque catégorie */}
                {period.themes && period.themes[themeKey] && period.themes[themeKey].map((theme: any, tIdx: number) => (
                  <div key={tIdx} className="text-gray-700 text-xs sm:text-sm mb-1 sm:mb-2">
                    {theme.label === '__custom__' 
                      ? theme.customLabel || <span className="italic text-gray-400 text-xs">Personnalisé</span>
                      : theme.label || <span className="italic text-gray-400 text-xs">-</span>}
                  </div>
                ))}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
) : (
  <div className="mt-4 bg-gray-50 p-4 rounded-sm text-gray-500 italic">
    Aucune période définie.
  </div>
)}
            </div>
          </div>
        </div>

        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            Vous êtes intéressé par ce type de programmation ? Créez votre compte pour créer et gérer vos propres programmations.
          </p>
          <div className="mt-4">
            <button
              type="button"
              onClick={() => navigate('/register')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Créer un compte
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicProgrammation;
